"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205219" className="flex gap-4">
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-4">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-default font-medium text-[#ffffff] transition-colors hover:text-[#ffffff]/80 focus:text-[#ffffff]/80 focus:outline-none whitespace-nowrap">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-ring transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full border-b border-border bg-background/95 p-4 shadow-lg backdrop-blur-md md:hidden">
      <nav className="flex flex-col gap-4">
        {loading ? <div className="flex flex-col gap-4">
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-base font-default font-medium text-foreground transition-colors hover:text-ring" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  return <section id="clinic-agatas-header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-[#264256]/90 backdrop-blur-md border-b border-white/10 shadow-sm" : "bg-[#264256] border-b border-transparent"}`}>
      <div className="relative">
        {/* Logotipas per abi juostas */}
        <div className="absolute left-4 sm:left-6 lg:left-8 top-0 h-[120px] flex items-center z-10">
          <Link to="/">
            <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/05/Agatas-logo_Horizontalus.-Spalvotas-sviesus.png" alt="Clinic Agatas" className="w-auto ![margin:5px_-10px_5px_-10px] ![padding:30px_30px_10px_0px] !h-[300px]" />
          </Link>
        </div>

        {/* Viršutinė juosta */}
        <div className="border-b border-white/10 bg-[#264256]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-10 items-center justify-end text-[11px] sm:text-xs">
              <div className="flex items-center gap-3 sm:gap-6 shrink-0">
                <span className="font-medium text-[#ffffff]">+353 000 0000</span>
                <Button size="sm" className="h-7 rounded-full bg-[#65d19d] text-[#09090B] hover:bg-[#65d19d]/90 font-medium px-4 text-[11px] shadow-none">
                  Book a consultation
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Pagrindinė juosta */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-end pl-64">
            <div className="hidden md:flex">
              <MenuProvider menu_id="38">
                <HeaderMenuConsumer />
              </MenuProvider>
            </div>

            <div className="md:hidden flex items-center">
              <Button variant="ghost" size="icon" onClick={toggleMobileMenu} aria-expanded={isMobileMenuOpen} className="text-[#ffffff] hover:text-[#ffffff]/80">
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <MenuProvider menu_id="38">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}